/**
 * Logger — lightweight structured logger with level filtering and an
 * optional remote sink. Captures unhandled errors and promise rejections
 * when `Logger.installGlobalHandlers()` is called.
 */

export type LogLevel = 'debug' | 'info' | 'warn' | 'error';
const LEVELS: Record<LogLevel, number> = { debug: 0, info: 1, warn: 2, error: 3 };

export interface LogEntry {
  level: LogLevel;
  message: string;
  timestamp: string;
  context?: Record<string, unknown>;
}

export type LogSink = (entry: LogEntry) => void;

class LoggerImpl {
  private minLevel: LogLevel = 'info';
  private sinks: LogSink[] = [];
  private defaultContext: Record<string, unknown> = {};

  constructor() {
    // Register the default console sink after class fields initialise so
    // TypeScript doesn't complain about using `consoleSink` before init.
    this.sinks = [this.consoleSink];
  }

  setLevel(level: LogLevel): void { this.minLevel = level; }
  setContext(context: Record<string, unknown>): void { this.defaultContext = { ...this.defaultContext, ...context }; }
  addSink(sink: LogSink): void { this.sinks.push(sink); }
  clearSinks(): void { this.sinks = [this.consoleSink]; }

  debug(message: string, context?: Record<string, unknown>): void { this.log('debug', message, context); }
  info(message: string, context?: Record<string, unknown>):  void { this.log('info',  message, context); }
  warn(message: string, context?: Record<string, unknown>):  void { this.log('warn',  message, context); }
  error(message: string, context?: Record<string, unknown>): void { this.log('error', message, context); }

  private log(level: LogLevel, message: string, context?: Record<string, unknown>): void {
    if (LEVELS[level] < LEVELS[this.minLevel]) return;
    const entry: LogEntry = {
      level,
      message,
      timestamp: new Date().toISOString(),
      context: { ...this.defaultContext, ...context },
    };
    for (const sink of this.sinks) {
      try { sink(entry); } catch { /* ignore sink errors */ }
    }
  }

  private consoleSink = (e: LogEntry): void => {
    const out = (e.level === 'error') ? console.error
              : (e.level === 'warn')  ? console.warn
              : (e.level === 'info')  ? console.info
              : console.debug;
    out(`[${e.timestamp}] [${e.level}] ${e.message}`, e.context ?? {});
  };

  /** Build a sink that POSTs NDJSON entries to a remote endpoint. */
  static remoteSink(url: string, getAuthHeader: () => Record<string, string> = () => ({})): LogSink {
    return (entry) => {
      try {
        fetch(url, {
          method: 'POST',
          keepalive: true,
          headers: { 'Content-Type': 'application/x-ndjson', ...getAuthHeader() },
          body: JSON.stringify(entry) + '\n',
        }).catch(() => { /* best-effort */ });
      } catch { /* ignore */ }
    };
  }

  installGlobalHandlers(): void {
    if (typeof window === 'undefined') return;
    window.addEventListener('error', (ev) => {
      this.error('Uncaught error', { message: ev.message, filename: ev.filename, line: ev.lineno, col: ev.colno });
    });
    window.addEventListener('unhandledrejection', (ev) => {
      this.error('Unhandled promise rejection', { reason: String(ev.reason) });
    });
  }
}

export const Logger = new LoggerImpl();
